import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Loader2, CheckCircle2, FileSpreadsheet, ArrowRight, Download } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BROKER_TEMPLATES, downloadBrokerTemplate } from '@/constants/brokerTemplates';

const API = process.env.REACT_APP_BACKEND_URL;
const BROKERS = ['OCTA', 'HFM', 'VANTAGE', 'XM', 'PEPPERSTONE', 'EXNESS', 'FBS', 'UNIVERSAL'];

export default function SegmentationImportWizard({ open, onOpenChange, ibId, onImported }) {
  const [broker, setBroker] = useState('HFM');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [basis, setBasis] = useState('cumulative');
  const [snapDate, setSnapDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const reset = () => { setFile(null); setPreview(null); setMapping({}); };

  const runPreview = async (chosenFile, sheet) => {
    const f = chosenFile || file;
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('broker', broker);
      if (sheet) fd.append('sheet', sheet);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/segmentation/${ibId}/upload/preview`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPreview(res.data);
      setMapping(res.data.mapping || {});
      setBasis(res.data.value_basis || 'cumulative');
      if (res.data.template_found) toast.success(`Template ${broker} dijumpai — mapping auto-isi`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal baca fail');
    } finally {
      setBusy(false);
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
    runPreview(f);
  };

  const commit = async () => {
    if (!mapping.account_no) { toast.error('Column Account / Login No wajib dipetakan'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('broker', broker);
      fd.append('mapping_json', JSON.stringify(mapping));
      fd.append('value_basis', basis);
      fd.append('save_template', 'true');
      if (!mapping.date) fd.append('snapshot_date', snapDate);
      if (preview?.sheet) fd.append('sheet', preview.sheet);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/segmentation/${ibId}/upload/commit`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${res.data.accounts} account diimport (${res.data.dates.join(', ')})`);
      reset();
      onImported?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import gagal');
    } finally {
      setBusy(false);
    }
  };

  const fields = preview?.fields || [];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="seg-import-wizard">
        <DialogTitle className="sr-only">Import Report Broker</DialogTitle>
        <DialogDescription className="sr-only">Import Report Broker</DialogDescription>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#E63946]/20 border border-[#E63946]/40">
            <Upload size={18} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Import Report Broker</h3>
            <p className="text-xs text-[hsl(var(--text-muted))]">CSV atau Excel · mapping column disimpan sebagai template per broker</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="text-[10px] uppercase font-bold tracking-wider text-[hsl(var(--text-muted))]">Broker</label>
            <Select value={broker} onValueChange={(v) => { setBroker(v); setPreview(null); }}>
              <SelectTrigger className="h-9 text-xs bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]" data-testid="seg-import-broker">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BROKERS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase font-bold tracking-wider text-[hsl(var(--text-muted))]">Fail report</label>
            <div className="flex items-center gap-2">
              <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" onChange={onPick} className="hidden" data-testid="seg-import-file" />
              <button onClick={() => inputRef.current?.click()}
                className="h-9 px-3 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] text-xs text-white flex items-center gap-2 hover:border-[#E63946]"
                data-testid="seg-import-pick">
                <FileSpreadsheet size={13} /> {file ? file.name : 'Pilih fail…'}
              </button>
              {busy && <Loader2 size={16} className="animate-spin text-[hsl(var(--text-muted))]" />}
            </div>
          </div>
        </div>

        {/* Template checklist + download */}
        <div className="mb-4 p-3 rounded-lg border border-[#7C3AED]/30 bg-[#7C3AED]/5" data-testid="seg-template-box">
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <Download size={13} className="text-[#a78bfa]" />
            <p className="text-xs font-semibold text-white">Template column untuk {broker}</p>
            <button onClick={() => downloadBrokerTemplate(broker)}
              className="ml-auto h-7 px-3 rounded-md border border-[#7C3AED]/50 bg-[#7C3AED]/15 text-[10px] font-bold uppercase tracking-wider text-[#c4b5fd] hover:bg-[#7C3AED]/25"
              data-testid="seg-template-download">
              Download template CSV
            </button>
          </div>
          <p className="text-[11px] text-[hsl(var(--text-muted))] mb-1.5">
            {(BROKER_TEMPLATES[broker] || BROKER_TEMPLATES.UNIVERSAL).note}
            {' '}Basis: <span className="text-white">{(BROKER_TEMPLATES[broker] || BROKER_TEMPLATES.UNIVERSAL).basis}</span>
          </p>
          <div className="flex flex-wrap gap-1">
            {(BROKER_TEMPLATES[broker] || BROKER_TEMPLATES.UNIVERSAL).columns.map((c) => (
              <span key={c} className="px-1.5 py-0.5 rounded text-[9px] font-mono border border-[hsl(var(--border))] text-[hsl(var(--text-muted))]">{c}</span>
            ))}
          </div>
        </div>

        {preview && (
          <>
            {preview.sheets?.length > 1 && (
              <div className="mb-4">
                <label className="text-[10px] uppercase font-bold tracking-wider text-[hsl(var(--text-muted))]">Sheet</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {preview.sheets.map((sh) => (
                    <button key={sh} onClick={() => runPreview(file, sh)}
                      className={`px-3 py-1.5 rounded-full text-[10px] font-bold uppercase border ${
                        preview.sheet === sh ? 'border-[#10b981] text-[#10b981] bg-[#10b981]/10'
                          : 'border-[hsl(var(--border))] text-[hsl(var(--text-muted))]'}`}
                      data-testid={`seg-import-sheet-${sh}`}>
                      {sh}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-4 p-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))]">
              <p className="text-xs text-white mb-2">
                {preview.row_count.toLocaleString()} baris · {preview.columns.length} column dijumpai
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-wider text-[hsl(var(--text-muted))]">Nilai dalam fail ni</label>
                  <Select value={basis} onValueChange={setBasis}>
                    <SelectTrigger className="h-9 text-xs bg-[hsl(var(--surface))] border-[hsl(var(--border))]" data-testid="seg-import-basis">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cumulative">Kumulatif / All-time (lot & komisen sejak mula)</SelectItem>
                      <SelectItem value="daily">Harian (nilai untuk hari tu sahaja)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {!mapping.date && (
                  <div>
                    <label className="text-[10px] uppercase font-bold tracking-wider text-[hsl(var(--text-muted))]">Tarikh snapshot (fail tiada column tarikh)</label>
                    <input type="date" value={snapDate} onChange={(e) => setSnapDate(e.target.value)}
                      className="w-full h-9 rounded-md bg-[hsl(var(--surface))] border border-[hsl(var(--border))] px-2 text-xs text-white"
                      data-testid="seg-import-date" />
                  </div>
                )}
              </div>
            </div>

            <p className="text-[10px] uppercase font-bold tracking-wider text-[hsl(var(--text-muted))] mb-2">Mapping column</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
              {fields.map((f) => (
                <div key={f.key} className="flex items-center gap-2">
                  <span className={`text-[11px] w-40 shrink-0 ${f.required ? 'text-white font-semibold' : 'text-[hsl(var(--text-muted))]'}`}>
                    {f.label}{f.required ? ' *' : ''}
                  </span>
                  <ArrowRight size={11} className="text-[hsl(var(--text-muted))] shrink-0" />
                  <Select value={mapping[f.key] || '__none__'}
                    onValueChange={(v) => setMapping((m) => {
                      const next = { ...m };
                      if (v === '__none__') delete next[f.key]; else next[f.key] = v;
                      return next;
                    })}>
                    <SelectTrigger className="h-8 text-[11px] bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]"
                      data-testid={`seg-map-${f.key}`}>
                      <SelectValue placeholder="— tiada —" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="__none__">— tiada —</SelectItem>
                      {preview.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            <div className="overflow-x-auto mb-4 rounded-lg border border-[hsl(var(--border))]">
              <table className="w-full text-[10px]">
                <thead>
                  <tr className="bg-[hsl(var(--surface-elevated))]">
                    {preview.columns.slice(0, 9).map((c) => (
                      <th key={c} className="text-left px-2 py-1.5 text-[hsl(var(--text-muted))] whitespace-nowrap">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.sample.map((r, i) => (
                    <tr key={i} className="border-t border-[hsl(var(--border))]/50">
                      {preview.columns.slice(0, 9).map((c) => (
                        <td key={c} className="px-2 py-1 text-white/80 whitespace-nowrap max-w-[140px] truncate">{String(r[c] ?? '')}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-end gap-2">
              <button onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-md text-xs text-[hsl(var(--text-muted))] hover:text-white">
                Batal
              </button>
              <button onClick={commit} disabled={busy}
                className="px-4 py-2 rounded-md text-xs font-semibold bg-[#E63946] text-white flex items-center gap-2 disabled:opacity-50"
                data-testid="seg-import-commit">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Import sekarang
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
